import React, { useState } from 'react';
import { Check, Copy, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import { unwrapParagraphs, type PromptFile, type PromptFor, type PromptPaste } from '../../utils/promptWriter';

interface PromptCardBodyProps {
  file: PromptFile;
  promptFor: PromptFor | null;
  /** Takes a paste to the canvas as a node; absent where the board has no canvas at hand */
  onSendToCanvas?: (paste: PromptPaste) => void;
}

/**
 * A prompt card's writing shown as the prompt file it is: the header's notes (the version note,
 * the shot list, the settings to set in the node, what to check), the references to attach in
 * order, and each paste in a well of its own with a button to copy it and one to take it to the
 * canvas as a node. The whole file is still the card's text, changed in place like any card's.
 */
export const PromptCardBody: React.FC<PromptCardBodyProps> = ({ file, promptFor, onSendToCanvas }) => {
  const [copied, setCopied] = useState<number | null>(null);
  const node = promptFor?.node ?? 'video';
  const nodeName = node === 'video' ? 'Video' : 'Image';
  const copy = async (e: React.MouseEvent, i: number, text: string) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(i);
      window.setTimeout(() => setCopied(current => (current === i ? null : current)), 1500);
    } catch { /* nothing to copy with: the text can be selected */ }
  };
  // The header without its PROJECT line: the card's head says the title
  const notes = unwrapParagraphs(file.header.split('\n').filter(line => !/^\s*PROJECT\b/i.test(line)).join('\n')).trim();

  return (
    <div data-prompt-card={node}>
      {notes && <div className="rounded-lg bg-ink/[0.04] px-3 py-2 text-xs leading-5 text-ink-soft whitespace-pre-wrap [overflow-wrap:anywhere]" data-prompt-notes>{notes}</div>}
      {file.references.length > 0 && (
        <div className="mt-3" data-prompt-references>
          <p className="ns-t-label">Attach to the node, in this order</p>
          <ul className="mt-1 flex flex-col gap-1">
            {file.references.map((ref, i) => (
              <li key={`${ref.tag}-${i}`} className="flex items-start gap-2 text-sm leading-6 text-ink">
                <span className="ns-chip ns-chip-sm ns-chip-lavender shrink-0 mt-0.5">{ref.tag}</span>
                <span className="[overflow-wrap:anywhere]">{ref.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {file.pastes.map((paste, i) => (
        <section key={i} className="mt-3" data-prompt-paste>
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="ns-t-label flex-1 truncate">{paste.label ?? 'The prompt'}</span>
            <button type="button" onClick={(e) => void copy(e, i, paste.text)} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat" title="Copy the prompt, to paste into a node" data-prompt-copy>
              {copied === i ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />} {copied === i ? 'Copied' : 'Copy'}
            </button>
            {onSendToCanvas && (
              <button type="button" onClick={(e) => { e.stopPropagation(); onSendToCanvas(paste); }} className="ns-btn ns-btn-xs ns-btn-primary ns-btn-flat" title={`A new ${nodeName} node on the canvas with this prompt and the settings it was written for`} data-prompt-to-canvas>
                {node === 'video' ? <VideoIcon size={12} aria-hidden="true" /> : <ImageIcon size={12} aria-hidden="true" />} To a {nodeName} node
              </button>
            )}
          </div>
          <div className="ns-well font-serif text-[15px] leading-7 text-ink whitespace-pre-wrap [overflow-wrap:anywhere]" data-prompt-paste-text>{paste.text}</div>
        </section>
      ))}
    </div>
  );
};

export default PromptCardBody;
